/**
 * useTemplateList — THE data contract for the template list (D-5, F-06).
 *
 * At the merged base there were two, and they disagreed about everything:
 *
 *   CustomerTemplates  re-ran `search(filters)` on every ticker delivery, but only when a
 *                      server-side filter was active (WS-D2's fix for the 2×-fetch-per-tick
 *                      bug); it kept NO cache, so every open painted a spinner.
 *   TemplatesPanel     painted from `readCache("templates.list")` first, consumed the
 *                      delivered payload directly, and kept the cache warm — but its
 *                      delivery handler ignored filters entirely, because it had none.
 *
 * This hook is the union, and both pages gain the half they were missing: the customer page
 * gains the instant cache paint, the admin page gains the filter-aware delivery.
 *
 * The fetch contract, stated once so it cannot drift again:
 *   - cache-first paint on mount, then EXACTLY ONE `listTemplates()` per mount;
 *   - the initial-load effect is keyed on `[client]` only — never on `filters`, which is
 *     what made the effect re-fire and took AUD-6's measured `/api/templates` count to 4 per
 *     `#browse` load;
 *   - a ticker delivery is consumed DIRECTLY when no server filter is active (the delivery
 *     IS the unfiltered list, and the tick has already paid for it), and triggers exactly one
 *     re-search when one IS active, where the delivery would be the wrong row set;
 *   - `writeCache` on every successful fetch;
 *   - `refresh()` stamps `noteManualSync()` so the freshness line moves with the data.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ProductTemplate } from "@genz/platform";
import { useServices } from "../app/context.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
import { noteManualSync, subscribeTemplates } from "../sync/autoSync.js";
import { templateErrorMessage } from "./templateErrors.js";
import { subscribeCatalogChanged } from "./catalogInvalidation.js";

export type LoadState = "loading" | "ready" | "error";

/** The browse filters (US4). Empty fields are omitted from the query. */
export interface Filters {
  brand: string;
  category_type: string;
  category: string;
  product_category: string;
}

export const EMPTY_FILTERS: Filters = { brand: "", category_type: "", category: "", product_category: "" };

/** The ONE cache key both pages share, so switching between them paints instantly. */
export const TEMPLATES_CACHE = "templates.list";

/** Build the listTemplates query, dropping blank fields so we never send `?brand=`. */
export function toQuery(f: Filters): Record<string, string> {
  const q: Record<string, string> = {};
  if (f.brand.trim()) q.brand = f.brand.trim();
  if (f.category_type.trim()) q.category_type = f.category_type.trim();
  if (f.category.trim()) q.category = f.category.trim();
  if (f.product_category.trim()) q.product_category = f.product_category.trim();
  return q;
}

/** Is any server-side filter set? */
export function hasServerFilter(f: Filters): boolean {
  return Object.values(f).some((v) => v.trim() !== "");
}

export interface TemplateList {
  templates: ProductTemplate[];
  state: LoadState;
  /** True once a network fetch has answered — `ready` alone may be the cached paint. */
  fetched: boolean;
  error: string | null;
  /** The active server-side filters, and the setter the filter form drives. */
  filters: Filters;
  setFilters: (next: Filters | ((prev: Filters) => Filters)) => void;
  /** Re-run the CURRENT server-side search and stamp the freshness clock. */
  refresh: () => Promise<void>;
  /** Run a NEW server-side search (Apply / Reset). */
  search: (f: Filters) => Promise<void>;
  /** Swap one row in place — sync / activate / approve, without a refetch. */
  replaceRow: (t: ProductTemplate) => void;
  /** Drop one row — delete-everywhere, without a refetch. */
  removeRow: (id: string) => void;
  setError: (msg: string | null) => void;
}

export function useTemplateList(): TemplateList {
  const { client } = useServices();
  const [templates, setTemplates] = useState<ProductTemplate[]>([]);
  const [state, setState] = useState<LoadState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  /** Read through a ref so applying a filter does not churn the subscription — WS-D2's
   *  pattern, kept verbatim because it is the fix that took the fetch count from 4 to 1. */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  /** The rows currently painted, mirrored for the SAME reason as `filtersRef`: the failure
   *  path below has to know whether anything is on screen, and reading it from a
   *  `setTemplates` updater put a side effect in a function React double-invokes under
   *  StrictMode (review-1 m-5). A ref answers the question without one. */
  const templatesRef = useRef(templates);
  templatesRef.current = templates;

  /** The session this hook mounted in: ticker deliveries and row edits write under it. */
  const [mountEpoch] = useState(cacheEpoch);

  /** Fetch for `f` and paint. Shared by the initial load, Apply/Reset and Refresh. */
  const fetchFor = useCallback(
    async (f: Filters, opts?: { stampFreshness?: boolean }) => {
      setError(null);
      const epoch = cacheEpoch();
      try {
        const rows = (await client.listTemplates(toQuery(f))) ?? [];
        setTemplates(rows);
        setState("ready");
        writeCache(TEMPLATES_CACHE, rows, epoch);
        // Only an UNFILTERED pull is the whole catalog, so only that one is worth stamping
        // as "this is how fresh everything is".
        if (opts?.stampFreshness) noteManualSync();
      } catch (e) {
        setError(templateErrorMessage(e, "load the templates"));
        // Never wipe rows we already have: offline/demo keeps showing the last good list
        // rather than flashing an empty page (TemplatesPanel's behaviour, now on both).
        //
        // But ALWAYS leave `loading` behind. `search` (Apply / Reset) flips ready → loading,
        // and leaving it there on a failure disabled the Refresh button — the one control
        // that could retry — and kept "Refreshing…" on screen under an error banner, until a
        // 60 s ticker delivery or a route change (review-1 I-2). Rows on screen → back to
        // `ready`, with the banner saying what failed; no rows → `error`.
        setState(templatesRef.current.length ? "ready" : "error");
      } finally {
        setFetched(true);
      }
    },
    [client],
  );

  const search = useCallback(
    async (f: Filters) => {
      setFilters(f);
      filtersRef.current = f;
      setState((s) => (s === "ready" ? "loading" : s));
      await fetchFor(f);
    },
    [fetchFor],
  );

  const refresh = useCallback(async () => {
    await fetchFor(filtersRef.current, { stampFreshness: true });
  }, [fetchFor]);

  // Initial load — cache-first paint, then EXACTLY ONE fetch. Keyed on `[client]` only.
  // `fetchFor` is stable for a given client, so StrictMode's double-invoke re-runs this
  // effect once and the guard below keeps the request count at one.
  const loadedForRef = useRef<unknown>(null);
  useEffect(() => {
    if (loadedForRef.current === client) return;
    loadedForRef.current = client;
    const cached = readCache<ProductTemplate[]>(TEMPLATES_CACHE);
    if (cached && cached.length) {
      setTemplates(cached);
      setState("ready");
    } else {
      setState("loading");
    }
    void fetchFor(EMPTY_FILTERS);
  }, [client, fetchFor]);

  // A ticker delivery. The ticker has ALREADY fetched the unfiltered list before it notifies,
  // so re-searching unconditionally made every tick cost a SECOND GET /api/templates — the
  // app's heaviest endpoint, once a minute per open client, on top of the one the tick just
  // paid for.
  useEffect(
    () =>
      subscribeTemplates((delivery) => {
        const active = filtersRef.current;
        if (hasServerFilter(active)) {
          void fetchFor(active);
          return;
        }
        setTemplates(delivery.templates);
        setState("ready");
        setError(null);
        writeCache(TEMPLATES_CACHE, delivery.templates, mountEpoch);
      }),
    [fetchFor, mountEpoch],
  );

  // A catalog change (a phone model or case style renamed, a template created or edited
  // elsewhere in this app) invalidates what the server resolved for us. `display.model` and
  // `display.style` are SERVER-resolved, so re-asking is the only correct response — there is
  // nothing the client could recompute. This is the ONE place besides the ticker and the
  // buttons where a second fetch is legitimate; it is not the F-06 regression.
  useEffect(() => subscribeCatalogChanged(() => { void refresh(); }), [refresh]);

  const replaceRow = useCallback((t: ProductTemplate) => {
    setTemplates((prev) => {
      const next = prev.map((row) => (row.templateId === t.templateId ? t : row));
      writeCache(TEMPLATES_CACHE, next, mountEpoch);
      return next;
    });
  }, [mountEpoch]);

  const removeRow = useCallback((id: string) => {
    setTemplates((prev) => {
      const next = prev.filter((row) => row.templateId !== id);
      writeCache(TEMPLATES_CACHE, next, mountEpoch); // keep the cache in sync so it won't reappear
      return next;
    });
  }, [mountEpoch]);

  return {
    templates, state, fetched, error, filters, setFilters,
    refresh, search, replaceRow, removeRow, setError,
  };
}
